<script setup lang="ts">
/*
 * V1.1.4 — persistent "Resume Payroll" pill in the top bar.
 *
 * Visible only when (a) an In-Progress payroll run exists for the
 * tenant and (b) the user is not already on a `/pay/run/*` route. Click
 * navigates to the run shell with the active run's PayrollId.
 *
 * Prototype anchor: `renderResumePayrollPill()` (line 5368+) and
 * `#resumePayrollPillHost` slot in the top bar (line 1010) of
 * Optimal-HR-Payroll-Module.html.
 *
 * The richer subtext from the prototype ("paused at Step N · X min
 * ago") will be wired in alongside Wave 2.2 (Run shell), which fetches
 * `/payrolls/{payrollId}/in-progress-summary` for the active step.
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { PlayCircle } from 'lucide-vue-next';
import { useActivePayrollRun } from '@/modules/pay/composables/useActivePayrollRun';

const route = useRoute();
const router = useRouter();

/**
 * Only fetch the payroll list while the user is inside the Payroll module.
 * The pill lives in the always-mounted top bar, so an unconditional fetch hit
 * `GET /payroll-svc/employers/{id}/payrolls` on Home right after sign-in.
 * TanStack Query keeps the cached result when disabled, so once the user has
 * visited Payroll the pill can still surface globally from cache — without
 * ever firing the request on a fresh Home.
 */
const inPayModule = computed(() => route.path.startsWith('/pay'));
const { activeRun, payrollId } = useActivePayrollRun({ enabled: inPayModule });

/**
 * Hide on any `/pay/run/*` page — the prototype hides the pill once the
 * user is already inside the run shell.
 */
const onRunPage = computed(() => route.path.startsWith('/pay/run/'));

const visible = computed(() => Boolean(activeRun.value && payrollId.value) && !onRunPage.value);

const runLabel = computed(() => activeRun.value?.Name ?? activeRun.value?.PayDate ?? '');

function onClick() {
  if (!payrollId.value) return;
  router.push({ name: 'pay-run', params: { payrollId: payrollId.value } });
}
</script>

<template>
  <button
    v-if="visible"
    type="button"
    class="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 transition hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
    :title="$t('v2.shell.resume-pill.title')"
    data-testid="resume-payroll-pill"
    @click="onClick"
  >
    <PlayCircle class="h-3.5 w-3.5" aria-hidden="true" />
    <span>
      {{ $t('v2.shell.resume-pill.label') }}
      <span v-if="runLabel" class="text-amber-900/80"> · {{ runLabel }}</span>
    </span>
  </button>
</template>
